/**
 * In-app notifications API client (backend path — flag NOTIFICATIONS_BACKEND).
 *
 * Reads the real /api/v1/notifications endpoints introduced in migration
 * 0130. Replaces the legacy client-side synthesis (poll /me/leaves +
 * /escalations, localStorage read-state) when the flag is ON.
 */

import { z } from 'zod'
import { apiRequest, apiRequestPaged } from './client'

const apiNotificationSchema = z.object({
  id: z.string(),
  kind: z.string(),
  message: z.string(),
  link: z.string().nullable(),
  relatedLeaveId: z.string().nullable(),
  createdAt: z.string(),
  readAt: z.string().nullable(),
  read: z.boolean(),
})

export type ApiNotification = z.infer<typeof apiNotificationSchema>

const unreadCountSchema = z.object({ count: z.number() })

/** First page of the current recipient's notifications, newest first. */
export async function listNotifications(limit = 20): Promise<ApiNotification[]> {
  const res = await apiRequestPaged<unknown>(`/api/v1/notifications?limit=${limit}`)
  return z.array(apiNotificationSchema).parse(res.data)
}

export async function getUnreadCount(): Promise<number> {
  const raw = await apiRequest<unknown>('/api/v1/notifications/unread-count')
  return unreadCountSchema.parse(raw).count
}

export async function markNotificationRead(id: string): Promise<void> {
  await apiRequest<unknown>(`/api/v1/notifications/${id}/read`, { method: 'POST' })
}

export async function markAllNotificationsRead(): Promise<void> {
  await apiRequest<unknown>('/api/v1/notifications/read-all', { method: 'POST' })
}
